import React, { useState } from 'react';
import {
  Box,
  CardHeader,
  Avatar,
  IconButton,
  Typography,
  Tooltip,
  CardContent,
  Button,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';

import PromptTable from './PromptTable';
import PromptEditor from './PromptEditor';

const Header = ({ icon, title, isEditing, setIsEditing }) => {
  const theme = useTheme();

  return (
    <Box display="flex" alignItems="center">
      <Avatar sx={{ mr: 1 }}>{icon || null}</Avatar>
      <Button
        onClick={() => isEditing && setIsEditing(false)}
        disabled={!isEditing}
        color="primary"
        sx={{
          fontSize: '16px',
          textTransform: 'none',
          color: theme => theme.palette.primary.main,
          '&.Mui-disabled': {
            color: theme =>
              theme.palette.mode === 'dark'
                ? theme.palette.grey[600]
                : theme.palette.grey[800],
          },
        }}
      >
        {title || 'Missing title'}
      </Button>
      {isEditing && (
        <>
          <Typography sx={{ mx: 1 }}>/</Typography>
          <Typography variant="body1" fontWeight="bold">
            EDIT
          </Typography>
        </>
      )}
    </Box>
  );
};

const UserPromptManager = ({ show, onClose, icon, title, setPromptCallBack }) => {
  const theme = useTheme();
  const loading = useSelector((state) => state.loading);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isNewPrompt, setIsNewPrompt] = useState(false);

  const handleClose = () => {
    setIsEditing(false);
    setIsNewPrompt(false);
    onClose();
  };

  const handleSelectPrompt = (prompt) => {
    setSelectedPrompt(prompt);
    setIsEditing(true);
    setIsNewPrompt(false);
  };

  const handleNewPrompt = () => {
    setSelectedPrompt({});
    setIsEditing(true);
    setIsNewPrompt(true);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 590,
        animation: 'fadeIn 0.2s ease-in',
        overflow: 'hidden',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(to bottom, #1e1e1e, #2e2e2e)'
            : 'linear-gradient(180deg, #fff, #e4f2fb, #f2f2f2)',
        boxShadow: 3,
        borderRadius: 2,
        animation: 'fadeIn 0.4s ease-in-out',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <CardHeader
        sx={{ pb: 0 }}
        action={
          <Tooltip title="Cancel">
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        }
        title={
          <Header
            icon={icon}
            title={title}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        }
      />
      <CardContent sx={{ px: 2, py: 1 }}>
        {isEditing ? (
          <PromptEditor
            prompt={selectedPrompt}
            onClose={() => {
              setSelectedPrompt(null);
              setIsEditing(false);
              setIsNewPrompt(false);
            }}
            isNewPrompt={isNewPrompt}
          />
        ) : (
          <PromptTable
            setPromptCallBack={setPromptCallBack}
            isLoading={loading}
            onSelectPrompt={handleSelectPrompt}
            onSelectNewPrompt={handleNewPrompt}
          />
        )}
      </CardContent>
    </Box>
  );
};

export default UserPromptManager;
